import { AnalyticsEvents } from '../../analytics/analytics.types.js';
import { useAnalytics } from '../../analytics/hooks/useAnalytics.js';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { resourceQueryPredicates } from '../../resources/queries/resourceQueryPredicates.js';
import { useQueryCacheSpaceHelpers } from '../utils/useQueryCacheSpaceHelpers.js';
import { useWoody } from '../../woody/woody.js';
import { toast } from '../../store/alerts.js';
import { useQueryUserInfo } from '../../user/queries/useQueryUserInfo.js';
import { withConfirmation } from '../../queries/utils/withConfirmation.js';
import { useQueryCachePermissionMemberHelpers } from '../../permission/queries/useQueryCachePermissionMembersHelpers.js';
import { removeItemsWithProvidedIdFromHistory } from '../../app/stores/userNavigationHistoryStore.js';

const useMutationDeleteOrLeaveSpace = () => {
  const { data: user } = useQueryUserInfo();
  const { client } = useWoody();
  const { track } = useAnalytics();
  const { deleteCachedSpaces } = useQueryCacheSpaceHelpers();
  const { removeSelfFromCachedSpace } = useQueryCachePermissionMemberHelpers();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ space }) => {
      if (!user) throw new Error("User not found");
      if (space.isOwner) {
        await client.v2({
          endpoint: "/v2/spaces/{spaceId}",
          params: {
            spaceId: space.id
          }
        }, {
          method: "delete"
        });
      } else {
        await client.v2({
          endpoint: "/v2/resources/{resourceId}/permissions/{userId}",
          params: {
            resourceId: space.id,
            userId: user.id
          }
        }, {
          method: "delete"
        });
      }
      return null;
    },
    onMutate: ({ space }) => {
      return {
        deleteCachedSpacesContext: deleteCachedSpaces([
          space.id
        ]),
        removeSelfFromCachedSpaceContext: removeSelfFromCachedSpace(space.id)
      };
    },
    onSuccess: (_, { space, disableToast, action = "space-preview" }) => {
      removeItemsWithProvidedIdFromHistory(space.id);
      if (!disableToast) toast({
        content: `Space ${space.isOwner ? "deleted" : "left"}`
      });
      track(space.isOwner ? AnalyticsEvents.DeletedSpace : AnalyticsEvents.LeftSpace, {
        spaceId: space.id,
        action
      });
      queryClient.invalidateQueries({
        predicate: (q) => (
          // Leaving a space means resources within the space are no longer available
          resourceQueryPredicates.resourceListAny(q) || resourceQueryPredicates.summaryAll(q)
        ),
        type: "active"
      });
    },
    onError: (_, { space, disableToast }, context) => {
      if (!disableToast) toast({
        content: `Failed to ${space.isOwner ? "delete" : "leave"} space`
      });
      context?.deleteCachedSpacesContext?.resetCacheToPreOptimisticState();
      context?.deleteCachedSpacesContext?.invalidateQueries();
      context?.removeSelfFromCachedSpaceContext?.resetCacheToPreOptimisticState();
      context?.removeSelfFromCachedSpaceContext?.invalidateQueries();
    }
  });
  return withConfirmation(mutation, ({ space }) => {
    return {
      title: `${space.isOwner ? "Delete" : "Leave"} space - Are you sure?`,
      content: `Are you sure you want to ${space.isOwner ? "delete" : "leave"} this space? ${space.isOwner ? "This action cannot be undone." : ""}`,
      confirmLabel: `Yes, ${space.isOwner ? "Delete" : "Leave"}`,
      cancelLabel: "Cancel",
      confirmButtonProps: {
        variant: "danger"
      }
    };
  });
};

export { useMutationDeleteOrLeaveSpace };
//# sourceMappingURL=useMutationDeleteOrLeaveSpace.js.map
